import { applyMiddleware, compose, createStore } from 'redux'
import logger from 'redux-logger'
import { Persistor, persistStore } from 'redux-persist'
import storageImport from 'redux-persist/lib/storage'
import * as Sentry from '@sentry/react'
import thunk from 'redux-thunk'
import { createRootReducer } from '../reducers'
import { diffMiddleware } from './socketServer/diffMiddleware' // Care: do not import from index, or circular deps crash in tests
import { serverApi } from './serverApi'
import sideEffectsMiddleware from './sideEffectsMiddleware'
import { outOfDateAnalysisMarkingMiddleware } from './flow'

import { FeedbotMiddleware } from './types'
import { exclusiveUiMiddleware } from './socketServer/exclusiveUiMiddleware'
import { botIntegrityMiddleware } from './flow/botIntegrityMiddleware'
import { ssmlMiddleware } from '../store/ssml/ssmlMiddleware'
import { uiIntegrityMiddleware } from './app/uiIntegrityMiddleware'
import { tryDockMiddleware } from './dock/sections/try/tryDockMiddleware'

// Handle CJS/ESM interop – Vite/Rollup builds don't unwrap `exports.default`
const storage: typeof storageImport = (storageImport as any).default ?? storageImport

export interface ExtendedPersistor extends Persistor {
  purgeExceptForKeys: (keysToPreserve: string[]) => Promise<void>
}

const _window = window as any

const composeEnhancers = _window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

export function configureStore() {
  const enhancers = getDefaultEnhancers()
  const enhancer = composeEnhancers(...enhancers)
  const rootReducer = createRootReducer()

  // @ts-ignore
  const store = createStore(rootReducer, {}, enhancer)
  const persistor = persistStore(store) as ExtendedPersistor

  persistor.purgeExceptForKeys = async (keysToPreserve: string[]) => {
    const savedStates = await Promise.all(
      keysToPreserve.map(async key => ({
        key: `persist:${key}`,
        value: await storage.getItem(`persist:${key}`)
      }))
    )

    await persistor.purge()

    await Promise.all(
      savedStates
        .filter(({ value }) => value !== null)
        .map(({ key, value }) => storage.setItem(key, value as string))
    )
  }

  return { store, persistor }
}

function getDefaultEnhancers() {
  const middlewares: FeedbotMiddleware[] = [
    thunk,
    diffMiddleware,
    serverApi.middleware,
    sideEffectsMiddleware,
    exclusiveUiMiddleware,
    outOfDateAnalysisMarkingMiddleware,
    botIntegrityMiddleware,
    uiIntegrityMiddleware,
    ssmlMiddleware,
    tryDockMiddleware
  ]

  if (_window.REDUX_LOGGER) {
    middlewares.push(logger)
  }

  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/redux/
  const sentryReduxEnhancer = Sentry.createReduxEnhancer({})

  return [applyMiddleware(...middlewares), sentryReduxEnhancer]
}
